/**
 * LessonPreview
 *
 * Reusable sequential clip player for lesson voiceover clips.
 * Presents all clips as a single unified timeline — scrubbing anywhere
 * jumps to the correct clip at the correct position.
 *
 * Mode 1 — In-memory (editor preview before saving):
 *   Pass `clips` + optional `notableSegments`
 *
 * Mode 2 — Firestore (published lesson view):
 *   Pass `context` + `originalVideoId` + `draftId`
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from '../firebase';
import { useScrubPause } from '../hooks/useScrubPause';
import type { VideoContext, VoiceoverNotableSegmentClip, NotableSegment } from '../types/videoEdit';
import { getDraft, getStorageUrl } from '../services/videoEditDraft';


// ── Types ──────────────────────────────────────────────────────────────────────

export interface LessonPreviewProps {
  // Mode 1: in-memory (editor)
  clips?: VoiceoverNotableSegmentClip[];
  notableSegments?: NotableSegment[];
  /**
   * Pre-loaded storage download URLs keyed by clip ID.
   * When provided, Mode 1.5 will skip fetching URLs for clips whose ID is
   * already present here (avoiding redundant Firebase Storage requests).
   * Useful when the caller pre-loads URLs before mounting (e.g. VideoDraftTest).
   */
  preloadedClipUrls?: Map<string, string>;

  // Mode 2: Firestore (published lesson)
  context?: VideoContext;
  originalVideoId?: string;
  draftId?: string;

  // Display options
  notes?: string;
  coachName?: string;
  /** Offering label, already formatted by the caller ("Single Lesson", "Lesson 2 of 6"). */
  offeringLabel?: string;
  /** ISO date the lesson was delivered. */
  deliveredAt?: string;
  downloadUrl?: string;
  /** Firebase Storage path for the composited video — used for download to avoid CORS. */
  downloadStoragePath?: string;
  /** Start playback at this specific clip */
  startClipId?: string;
  showControls?: boolean;
  autoPlay?: boolean;
  className?: string;
  onEnded?: () => void;
  /**
   * Visual variant for the player chrome.
   * - 'classic' (default) — legacy controls bar BELOW the video, blue scrubber accent.
   *   Used by the editor preview modal so the recording experience stays untouched.
   * - 'native' — native-HTML5-feeling controls overlaid on the video, grey scrubber,
   *   poster frame seek, fullscreen button. Used by all delivered playback contexts.
   */
  variant?: 'classic' | 'native';
  /** Called when the user clicks the fullscreen expand/collapse button (native variant only) */
  onFullscreenToggle?: () => void;
  /** When true, renders the fullscreen-collapse (compress) icon instead of the expand icon */
  isFullscreen?: boolean;
  /**
   * If set, seeks to this position (in seconds on the unified timeline) once
   * clip durations are resolved. Used by LessonEditPreview to restore a saved
   * playback position. Ignored if ≤ 0.
   */
  initialVirtualTime?: number;
  /**
   * Called on every video timeupdate with the current unified-timeline position
   * and total duration (both in seconds). Callers should throttle saves.
   */
  onVirtualTimeChange?: (virtualTime: number, totalDuration: number) => void;
  /**
   * Called when the video pauses (user action or end-of-clip transition).
   * Fires with the exact virtual time at the moment of pause — callers should
   * save immediately, as this is a natural "I stopped watching" checkpoint.
   */
  onPaused?: (virtualTime: number, totalDuration: number) => void;
  /**
   * Fires whenever playback starts or stops, with the new state. For chrome
   * outside the player that should track it — the delivered screen hides its
   * BREAKDOWN badge during playback so it never overlaps the camera bubble.
   */
  onPlayingChange?: (playing: boolean) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(s: number): string {
  if (!isFinite(s) || isNaN(s) || s < 0) return '--:--';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/* ── Native chrome geometry ────────────────────────────────────────────────
   The video fills its frame and the chrome sits over the picture, which is what
   YouTube does. Reserving clearance for the bar was tried and reverted: a
   contained video loses width with height, so 74px off the bottom cost 16% of
   the picture in both dimensions and put black on all four sides. The scrim is
   what keeps the scrubber readable, not clearance.

   The bar is 59, which is YouTube's .ytp-chrome-bottom exactly. */
const CONTROL_BAR_H = 59;
/** Below this, a seek is a no-op assignment the element will not acknowledge. */
const SEEK_EPSILON = 0.005;
/**
 * How the bar's 59 is divided.
 *
 * The scrubber used to start at the bar's top edge, which put it within a few
 * pixels of the picture's bottom edge — close enough that on a letterboxed clip
 * the track and the frame's edge read as one line. Moving it down inside the bar
 * costs nothing elsewhere: the bar does not move, the video does not shrink.
 *
 * The slack is fixed and small: 59 less the 6 scrub row less the 36 buttons
 * leaves 17 to distribute, and two things want it. The buttons need room above
 * the screen edge, and 4 is what is left after this — YouTube leaves about 3
 * under theirs. The 12px handle overhangs the scrub row by 3 at the bottom, so
 * at a 9 drop it clears the button boxes by 1, measured. Those boxes are
 * invisible until hovered, so nothing touches at rest; a hover circle under the
 * handle comes within that pixel. Trading a pixel of drop back would buy a pixel
 * of gap.
 *
 * 9 is the most that leaves the buttons off the edge, and it puts ~16px between
 * the picture's bottom edge and the track on a 16:9 clip against the ~17 asked
 * for. A wider clip letterboxes less and gets less. Reaching 17 on every clip
 * would need the bar taller or the video smaller, and both are excluded.
 */
const SCRUBBER_TOP_INSET = 9;
const SCRUBBER_ROW_H = 6;
/** Buttons are 36 and `items-center`, so this leaves 4 above and 4 below them. */
const BUTTON_ROW_H = CONTROL_BAR_H - SCRUBBER_TOP_INSET - SCRUBBER_ROW_H;

/**
 * The scrim behind the control bar.
 *
 * YouTube's .ytp-gradient-bottom, measured at 1372px: 98 tall (a 61 box plus 37
 * of padding-top), painted from a 1x198 PNG anchored to the bottom, so only its
 * lower 98 rows show. Decoding that PNG's alpha gives the ramp below — an
 * ease-in reaching 0.667 black at the very bottom.
 *
 * Ours keeps that measured shape and stretches it: 124 tall, scaled to 0.85 at
 * the bottom. Matching YouTube outright was tried against the brief and fails
 * it. Their curve is thin where our scrubber lives — the scrubber row sits 53
 * above the bar's bottom, which lands at 0.145 black on their ramp, where a
 * white handle over a sunlit court reads at 1.4:1. The stretch puts that row at
 * 0.32, sampled from a screenshot over a white frame rather than computed.
 *
 * That is near the crossover, which is where the height comes from: the handle
 * is white and the played fill is biscayne, so darkening the backdrop helps one
 * and hurts the other. At 0.32 they read 2.24:1 and 2.66:1, and no alpha does
 * much better for the worse of the two. Neither reaches 3:1, and no scrim can
 * get them there — that needs the scrubber's own colours, which is a separate
 * change.
 *
 * The stops are YouTube's decoded values normalised and rescaled, not values
 * chosen by eye. The height and the 0.85 maximum are ours.
 */
const SCRIM_H = 124;
const SCRIM_GRADIENT =
  'linear-gradient(to bottom,' +
  ' rgba(0,0,0,0) 0%,' +
  ' rgba(0,0,0,0.020) 14.3%,' +
  ' rgba(0,0,0,0.075) 28.6%,' +
  ' rgba(0,0,0,0.180) 42.9%,' +
  ' rgba(0,0,0,0.340) 57.1%,' +
  ' rgba(0,0,0,0.530) 71.4%,' +
  ' rgba(0,0,0,0.715) 85.7%,' +
  ' rgba(0,0,0,0.850) 100%)';

/**
 * A row in the settings menu.
 *
 * Deliberately generic: a row is a label, the current value as a string, and a
 * list of options that each know how to select themselves. Adding stable volume
 * or captions later means pushing another object into `settingsRows` — the
 * panel, the anchoring, the dismissal and the back navigation are all written
 * against this shape and none of them need to know what a row controls.
 *
 * A boolean setting fits as a two-option row (On / Off). The second shape this
 * needed turned out to be an ACTION — picture-in-picture, which holds no value
 * and offers no options, it just happens. `action` is that: a row with one set
 * runs it and closes the menu, and gets neither a value column nor a chevron,
 * because both would promise a submenu that does not exist.
 */
/**
 * Whether this browser can decode WebM in a media element at all. iPhone
 * Safari returns '' here: the recorded clips are MediaRecorder WebM
 * (VP9/VP8 + Opus), which it cannot play — each one fires `error` instead of
 * `loadedmetadata`. Evaluated once; a browser does not change its mind.
 */
const CAN_PLAY_WEBM =
  typeof document !== 'undefined' &&
  document.createElement('video').canPlayType('video/webm') !== '';

/**
 * Touch-first device: no hover and a coarse pointer. Drives the touch control
 * model below — tap-to-reveal, no mousemove or hover dependency. Deliberately
 * not user-agent sniffing, so a desktop with a touchscreen and a mouse keeps
 * the hover chrome.
 */
const IS_TOUCH =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches;

const SPEED_OPTIONS = [
  { value: 0.25, label: '0.25' },
  { value: 0.5, label: '0.5' },
  { value: 1, label: 'Normal' },
  { value: 1.5, label: '1.5' },
  { value: 2, label: '2' },
];

type SettingRow = {
  key: string;
  label: string;
  /** Shown right-aligned on the root row, before the chevron. Absent on an action. */
  valueLabel?: string;
  options?: { key: string; label: string; selected: boolean; onSelect: () => void }[];
  /** Set instead of `options` for a row that does a thing rather than holding a value. */
  action?: () => void;
};

/**
 * A chapter's thumbnail.
 *
 * Almost always free: ClipEditorModal already captures a 160x90 JPEG from the
 * composite canvas when a recording is saved, and that data URL survives the
 * upload and both load paths, so a delivered lesson arrives with a picture per
 * clip and nothing has to be decoded. The generation path below exists only for
 * clips recorded before that field, and for the case where the canvas was not
 * available at save time.
 *
 * Generation is cached at module scope by clip id, so reopening the panel — or
 * opening it in a second lesson and coming back — never decodes the same clip
 * twice. The cache lives as long as the page: it is not persisted, and a reload
 * clears it. That is the right lifetime for something this cheap to rebuild and
 * this stale-able; storage URLs are signed and rotate.
 */
const chapterThumbCache = new Map<string, string>();

/**
 * Where in the clip to capture. The first frame of a recording is frequently
 * black — the composite canvas has not painted by the time the encoder takes
 * frame one — so capturing at t=0 gives a black row. A second and a half in is
 * past that on every recording seen so far.
 *
 * Clips shorter than the offset take their own midpoint instead, which for a
 * two second clip is one second and still clear of the black opening. The
 * `duration - 0.05` clamp is what keeps a seek from landing past the end, where
 * some browsers never fire `seeked` at all.
 */
const THUMB_CAPTURE_AT = 1.5;
const THUMB_W = 160;
const THUMB_H = 90;

function ChapterThumb({ clipId, url, storedDataUrl, duration }: {
  clipId: string;
  url?: string;
  storedDataUrl?: string;
  duration: number;
}) {
  const [src, setSrc] = useState<string | null>(
    storedDataUrl ?? chapterThumbCache.get(clipId) ?? null,
  );

  useEffect(() => {
    if (src || !url) return;
    let cancelled = false;
    const v = document.createElement('video');
    /* Same as ClipThumbnail: anonymous CORS, metadata only. A storage URL that
       is not CORS-approved fails to load here rather than tainting the canvas,
       so the row falls back to its placeholder instead of throwing on export. */
    v.crossOrigin = 'anonymous';
    v.preload = 'metadata';
    v.muted = true;
    const cleanup = () => { v.src = ''; v.load(); };
    v.onloadedmetadata = () => {
      const d = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration;
      v.currentTime = d > 0
        ? Math.max(0, Math.min(THUMB_CAPTURE_AT, d / 2, d - 0.05))
        : 0;
    };
    v.onseeked = () => {
      if (cancelled) { cleanup(); return; }
      const c = document.createElement('canvas');
      c.width = THUMB_W; c.height = THUMB_H;
      let data: string | null = null;
      try {
        c.getContext('2d')?.drawImage(v, 0, 0, THUMB_W, THUMB_H);
        data = c.toDataURL('image/jpeg', 0.7);
      } catch { data = null; }
      cleanup();
      if (cancelled || !data) return;
      chapterThumbCache.set(clipId, data);
      setSrc(data);
    };
    v.onerror = cleanup;
    v.src = url;
    v.load();
    return () => { cancelled = true; cleanup(); };
  }, [src, url, clipId, duration]);

  return src
    ? <img src={src} alt="" className="w-full h-full object-cover" />
    : <div className="w-full h-full bg-black" />;
}

/** A segment presented as a chapter: one bar on the scrubber and one name in
 *  the control bar, spanning every consecutive clip recorded against it. */
type Chapter = {
  index: number;
  /** undefined only for the unreachable no-segment case. */
  segmentId?: string;
  startVirtual: number;
  endVirtual: number;
  label?: string;
};

function getClipLabel(clip: VoiceoverNotableSegmentClip, segments: NotableSegment[]): string {
  if (clip.label) return clip.label;
  if (clip.isCameraSource) return 'Camera';
  if (clip.segmentIds.length === 0) return 'Clip';
  const labels = clip.segmentIds
    .map((id) => segments.find((s) => s.id === id)?.label)
    .filter(Boolean) as string[];
  return labels.length > 0 ? labels.join(' + ') : 'Clip';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LessonPreview({
  clips: propClips,
  notableSegments: propSegments = [],
  preloadedClipUrls,
  context,
  originalVideoId,
  draftId,
  notes,
  coachName,
  offeringLabel,
  deliveredAt,
  downloadUrl,
  downloadStoragePath,
  startClipId,
  showControls = true,
  autoPlay = false,
  className = '',
  onEnded,
  variant = 'classic',
  onFullscreenToggle,
  isFullscreen = false,
  initialVirtualTime = 0,
  onVirtualTimeChange,
  onPaused,
  onPlayingChange,
}: LessonPreviewProps) {
  const isNative = variant === 'native';
  /**
   * Single-source mode: the element plays the composited MP4 (`downloadUrl`
   * is coachVideoUrl) as its one src. The MP4 is the clips concatenated in
   * order, so the virtual timeline maps 1:1 onto its currentTime — chapters
   * keep their cumulative offsets from the stored clip durations, and every
   * seek goes straight to the element.
   *
   * Active on EVERY touch device, not only where canPlayType('video/webm')
   * is empty: iOS Safari answers "maybe" for WebM and then fails to decode
   * the MediaRecorder clips, so the capability probe alone cannot be trusted
   * there — and on iPhone the system fullscreen player presents whatever the
   * element holds, which must be the whole lesson, not one clip. Desktop
   * keeps the per-clip player unless it genuinely cannot play WebM. Known
   * synchronously from props, so the per-clip loading machinery is gated off
   * before it can touch a WebM src.
   */
  const singleSource = (IS_TOUCH || !CAN_PLAY_WEBM) && !!downloadUrl;
  const [showOverlay, setShowOverlay] = useState(false);
  // ── Resolved clip list
  const [clips, setClips] = useState<VoiceoverNotableSegmentClip[]>(propClips ?? []);
  const [segments, setSegments] = useState<NotableSegment[]>(propSegments);
  // Storage URLs: initialised from preloadedClipUrls when provided (Mode 1 with
  // pre-fetched URLs), or populated lazily by Mode 1.5 / Mode 2 effects.
  const [clipUrls, setClipUrls] = useState<Map<string, string>>(preloadedClipUrls ?? new Map());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Playback state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);


  // ── Clip durations — probed upfront for all clips
  const [clipDurations, setClipDurations] = useState<Map<string, number>>(new Map());
  const [isProbing, setIsProbing] = useState(false);
  // Incremented in handleScrubCommit to force the clip-loading effect to re-run
  // after a cross-clip drag even when currentIndex didn't change in state.
  const [clipLoadTrigger, setClipLoadTrigger] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  // Object URLs we created — revoked on unmount
  const objectUrlsRef = useRef<string[]>([]);
  // Cached resolved URL per clip.id (prevents duplicate object URLs)
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  // If set, the clip-loading effect seeks to this time after load
  const seekOnLoadRef = useRef<{ time: number; play: boolean } | null>(null);
  // Tracks which clip ID was last loaded into the video element.
  // Used to prevent Mode 1.5 HTTP URL upgrades from reloading a clip that's
  // already loaded (which would abort any pending video.play() call).
  const loadedClipIdRef = useRef<string | null>(null);
  // Stable mirror of isPlaying for reading inside effects
  const isPlayingRef = useRef(false);
  isPlayingRef.current = isPlaying;
  // Stable mirror of autoPlay so the loading effect always reads the live value
  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;
  // True while the user is dragging the scrubber — suppresses the clip-loading
  // effect so rapid onChange events don't spam async video reloads.
  const isScrubbingRef = useRef(false);
  // Suppresses spurious onPause events fired by the programmatic video.pause()
  // call inside the clip-loading effect. Without this, changing clips calls
  // video.pause() on the previously-loaded element, which fires onPause and
  // overwrites the saved Firestore position with the old clip's currentTime.
  const isClipLoadingRef = useRef(false);
  // Stable mirrors of callback props and computed timeline values so that
  // handleScrubCommit (empty deps) can propagate scrub-end positions to the
  // parent without listing these as dependencies.
  const onPausedRef = useRef(onPaused);
  onPausedRef.current = onPaused;
  const onVirtualTimeChangeRef = useRef(onVirtualTimeChange);
  onVirtualTimeChangeRef.current = onVirtualTimeChange;
  // Ref-mirrored like the callbacks above, so the notifying effect keys on
  // isPlaying alone and an unstable callback prop cannot re-fire it.
  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;
  const clipOffsetsRef = useRef<number[]>([]);
  const totalDurationRef = useRef(0);
  // Stores the committed cross-clip target while the user is dragging.
  const pendingScrubRef = useRef<{ idx: number; time: number } | null>(null);

  // ── URL resolution
  // clipUrls holds HTTP storage URLs (Mode 1.5 / Mode 2). urlCacheRef holds
  // blob: URLs created from videoBlob. HTTP URLs take priority so that after
  // Mode 1.5 completes, subsequent clip navigations use the HTTP URL directly.
  const getUrl = useCallback((clip: VoiceoverNotableSegmentClip): string | null => {
    if (clipUrls.has(clip.id)) return clipUrls.get(clip.id)!;
    if (urlCacheRef.current.has(clip.id)) return urlCacheRef.current.get(clip.id)!;
    if (clip.videoBlob) {
      const url = URL.createObjectURL(clip.videoBlob);
      objectUrlsRef.current.push(url);
      urlCacheRef.current.set(clip.id, url);
      return url;
    }
    return null;
  }, [clipUrls]);

  // ── Compute initial index from startClipId
  const resolveStartIndex = useCallback((list: VoiceoverNotableSegmentClip[]) => {
    if (!startClipId) return 0;
    const idx = list.findIndex((c) => c.id === startClipId);
    return idx >= 0 ? idx : 0;
  }, [startClipId]);

  // ── Mode 1: keep in sync with prop changes
  useEffect(() => {
    if (propClips) {
      setClips(propClips);
      setCurrentIndex(resolveStartIndex(propClips));
    }
  }, [propClips, resolveStartIndex]);

  useEffect(() => {
    if (propSegments) setSegments(propSegments);
  }, [propSegments]);

  // ── Mode 1.5: fetch storage download URLs for clips that have videoStoragePath.
  // This includes clips that also have a videoBlob (e.g. after auto-save) so
  // that getUrl returns the HTTP URL and subsequent clip navigations bypass the
  // blob URL entirely.
  useEffect(() => {
    if (!propClips) return;
    const clipsNeedingUrl = propClips.filter(
      (c) => c.videoStoragePath && !clipUrls.has(c.id)
    );
    if (clipsNeedingUrl.length === 0) return;

    let cancelled = false;
    Promise.all(
      clipsNeedingUrl.map(async (c) => {
        try {
          const url = await getStorageUrl(c.videoStoragePath!);
          return [c.id, url] as [string, string];
        } catch (err) {
          console.warn('LessonPreview: failed to fetch URL for clip', c.id, err);
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const entries = results.filter((r): r is [string, string] => r !== null);
      if (entries.length > 0) {
        setClipUrls((prev) => {
          const next = new Map(prev);
          entries.forEach(([id, url]) => next.set(id, url));
          return next;
        });
      }
    });

    return () => { cancelled = true; };
  }, [propClips]);

  // ── Mode 2: load from Firestore
  useEffect(() => {
    if (propClips || !context || !originalVideoId || !draftId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const draft = await getDraft(context, originalVideoId, draftId);
        if (!draft || cancelled) return;

        // Fetch download URLs for voiceover clips only — skip old-style
        // recordingSegments / annotationSnapshots / audioRecordings that
        // loadDraftAssets() would fetch unnecessarily (LessonPreview doesn't
        // use them). Fewer Firebase Storage requests = less auth-token overhead.
        const loadedClips: VoiceoverNotableSegmentClip[] = draft.voiceoverNotableSegmentClips.map(
          (c) => ({ ...c })
        );
        const urlEntries = await Promise.all(
          loadedClips
            .filter((c) => c.videoStoragePath)
            .map(async (c) => {
              try {
                const url = await getStorageUrl(c.videoStoragePath!);
                return [c.id, url] as [string, string];
              } catch {
                return null;
              }
            })
        );
        if (cancelled) return;

        const voiceoverClipUrls = new Map<string, string>();
        urlEntries.forEach((e) => { if (e) voiceoverClipUrls.set(e[0], e[1]); });

        setClips(loadedClips);
        setSegments(draft.notableSegments ?? []);
        setClipUrls(voiceoverClipUrls);
        setCurrentIndex(resolveStartIndex(loadedClips));
      } catch (err) {
        if (!cancelled) setLoadError('Failed to load lesson preview.');
        console.error('LessonPreview: load error', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [propClips, context, originalVideoId, draftId, resolveStartIndex]);

  // ── Cleanup object URLs on unmount
  // Also clear urlCacheRef and reset loadedClipIdRef so that React Strict Mode's
  // simulated unmount→remount doesn't leave stale revoked blob URLs in the cache.
  // Without this, the second mount would find a revoked URL in urlCacheRef and
  // the loadedClipIdRef guard would prevent creating a fresh one, causing
  // video.play() to fail silently on the first clip.
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
      urlCacheRef.current.clear();
      loadedClipIdRef.current = null;
    };
  }, []);

  // ── Probe all clip durations upfront (sequential, single element)
  // MediaRecorder WebM blobs report duration=Infinity so we use the
  // seek-to-MAX_SAFE_INTEGER trick. We probe one clip at a time to avoid
  // race conditions, and guard every probe with an onerror + timeout fallback
  // so the "Calculating…" state can never get stuck.
  //
  // Optimisation: clips loaded from Firestore carry a stored `videoDuration`
  // field — use that directly so we never need to probe them.
  useEffect(() => {
    if (clips.length === 0) return;

    // Pre-seed durations from the stored videoDuration field (Firestore clips)
    const seeded = new Map<string, number>();
    for (const c of clips) {
      if ((c.videoDuration ?? 0) > 0) seeded.set(c.id, c.videoDuration!);
    }

    // Only probe clips that don't have a stored duration
    const toProbe = clips.filter(
      (c) => (seeded.get(c.id) ?? clipDurations.get(c.id) ?? 0) === 0
    );

    if (toProbe.length === 0) {
      // All durations known — merge seeded values into state
      if (seeded.size > 0) {
        setClipDurations((prev) => {
          const next = new Map(prev);
          seeded.forEach((v, k) => next.set(k, v));
          return next;
        });
      }
      return;
    }

    setIsProbing(true);
    let cancelled = false;
    const durations = new Map<string, number>(seeded);

    const probe = document.createElement('video');
    probe.muted = true;
    probe.preload = 'metadata';

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearHandlers = () => {
      probe.onloadedmetadata = null;
      probe.onseeked = null;
      probe.onerror = null;
      if (timeoutId !== null) { clearTimeout(timeoutId); timeoutId = null; }
    };

    const probeClip = (idx: number) => {
      if (cancelled || idx >= toProbe.length) {
        probe.src = '';
        if (!cancelled) {
          setClipDurations((prev) => {
            const next = new Map(prev);
            durations.forEach((v, k) => next.set(k, v));
            return next;
          });
          setIsProbing(false);
        }
        return;
      }

      const clip = toProbe[idx];
      const url = getUrl(clip);

      if (!url) {
        durations.set(clip.id, 0);
        probeClip(idx + 1);
        return;
      }

      // Only probe blob: URLs. Setting probe.src to an HTTP URL starts the
      // browser's media pipeline (download, buffer, decode) even before any
      // seek — that's the source of CPU spin in Mode 2 / VideoDraftTest.
      // HTTP clips without a stored videoDuration will show "--:--"; clips
      // recorded with the current code always store videoDuration and skip
      // the probe entirely via the seeded-duration path above.
      if (!url.startsWith('blob:')) {
        durations.set(clip.id, 0);
        probeClip(idx + 1);
        return;
      }

      const next = (dur: number) => {
        clearHandlers();
        if (!cancelled) durations.set(clip.id, Math.max(0, dur));
        probeClip(idx + 1);
      };

      // Fallback: if nothing fires within 5 s, mark duration as 0 and continue
      timeoutId = setTimeout(() => next(0), 5000);

      probe.onerror = () => next(0);

      probe.onloadedmetadata = () => {
        if (cancelled) return;
        if (isFinite(probe.duration) && probe.duration > 0) {
          next(probe.duration);
        } else {
          // Seek trick for Infinity-duration WebM blobs from MediaRecorder.
          // We only reach this branch for blob: URLs (HTTP is skipped above).
          probe.onseeked = () => next(probe.currentTime);
          probe.currentTime = Number.MAX_SAFE_INTEGER;
        }
      };

      probe.src = url;
      probe.load();
    };

    probeClip(0);

    return () => {
      cancelled = true;
      clearHandlers();
      probe.src = '';
      probe.load(); // abort any pending media pipeline (seek/decode) to stop CPU spin
    };
    // getUrl is a dependency because in Firestore mode, clipUrls loads async
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips, getUrl]);

  // ── Derived timeline values
  // Fall back to clip.videoDuration for clips whose duration was seeded from
  // Firestore but not yet written into the clipDurations Map.
  const getDur = useCallback(
    (clip: VoiceoverNotableSegmentClip) =>
      clipDurations.get(clip.id) ?? clip.videoDuration ?? 0,
    [clipDurations]
  );

  const clipOffsets = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    for (const clip of clips) {
      offsets.push(offset);
      offset += getDur(clip);
    }
    return offsets;
  }, [clips, getDur]);

  const totalDuration = useMemo(
    () => clips.reduce((sum, c) => sum + getDur(c), 0),
    [clips, getDur]
  );

  clipOffsetsRef.current = clipOffsets;
  totalDurationRef.current = totalDuration;

  /* Single-source: the MP4 can run a hair past the stored-duration total
     (transcode rounding), so clamp — the scrubber handle and chapter fills
     read this and must not paint past the track. */
  const virtualTimeRaw = (clipOffsets[currentIndex] ?? 0) + currentTime;
  const virtualTime = singleSource ? Math.min(virtualTimeRaw, totalDuration) : virtualTimeRaw;

  // ── Load current clip into the video element
  const currentClip = clips[currentIndex] ?? null;

  useEffect(() => {
    // Single-source mode never loads per-clip srcs — the composited MP4 is
    // loaded once by its own effect below.
    if (singleSource) return;
    // Don't reload while the user is dragging the scrubber — each onChange event
    // would otherwise restart the async media pipeline and the seek never lands.
    // handleScrubCommit fires on pointerup and commits the actual load+seek.
    if (isScrubbingRef.current) return;

    const video = videoRef.current;
    if (!video || !currentClip) return;

    const url = getUrl(currentClip);
    if (!url) return;

    // Guard: skip if this clip's src is already set in the video element.
    if (loadedClipIdRef.current === currentClip.id && video.src) {
      return;
    }

    const seekOn = seekOnLoadRef.current;
    seekOnLoadRef.current = null;
    const seekTime = seekOn?.time ?? 0;
    const shouldPlay = autoPlayRef.current || isPlayingRef.current || (seekOn?.play ?? false);

    // Do NOT touch video.src here when playback isn't needed yet.
    // Even setting video.src with preload="none" causes Chrome to follow
    // redirects / make HEAD requests for Firebase Storage URLs, which spins
    // up the media pipeline and causes visible CPU usage before the user
    // ever clicks play. Instead, defer the src assignment to handleTogglePlay.
    // EXCEPT for the 'native' variant — delivered playback wants a real
    // poster frame on every clip change. We eagerly load and seek to 0.1s
    // programmatically (in handleMeta below) so a keyframe paints — no URL
    // fragment, because some browsers render a small seek-time indicator
    // overlay when a media fragment like #t=0.1 is present.
    if (!isNative && !shouldPlay && seekTime === 0) {
      return;
    }

    const isPosterLoad = isNative && !shouldPlay && seekTime === 0;

    // We're actually about to load — mark this clip as loaded now.
    loadedClipIdRef.current = currentClip.id;

    isClipLoadingRef.current = true;
    video.pause();
    video.src = url;
    video.load();

    let cancelled = false;

    const handleMeta = () => {
      isClipLoadingRef.current = false;
      if (cancelled) return;

      let primed = false;
      const doPlay = () => {
        if (cancelled || primed) return;
        primed = true;
        if (autoPlayRef.current || isPlayingRef.current || (seekOn?.play ?? false)) {
          video.play().catch(() => {});
        }
      };

      if (seekTime > 0) {
        // Explicit seek (e.g. user scrubbed to a position in a different clip)
        video.currentTime = seekTime;
        video.addEventListener('seeked', doPlay, { once: true });
        video.addEventListener('canplay', doPlay, { once: true });
      } else if (isPosterLoad) {
        // Native variant idle-load: seek to 0.1s so a real frame paints
        // (avoiding the black-element default). Don't auto-play.
        video.currentTime = 0.1;
      } else {
        doPlay();
      }
    };

    video.addEventListener('loadedmetadata', handleMeta, { once: true });

    return () => {
      cancelled = true;
      isClipLoadingRef.current = false;
      video.removeEventListener('loadedmetadata', handleMeta);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentClip?.id, clipUrls, clipLoadTrigger]);

  // ── Navigation
  const goToClip = useCallback((idx: number, shouldPlay = false) => {
    if (singleSource) {
      /* One file: a clip jump is a seek to that clip's cumulative offset. */
      const t = clipOffsetsRef.current[idx] ?? 0;
      const video = videoRef.current;
      if (video) {
        video.currentTime = t;
        if (shouldPlay) video.play().catch(() => {});
        else video.pause();
      }
      setCurrentTime(t);
      setIsPlaying(shouldPlay);
      return;
    }
    const video = videoRef.current;
    if (video) video.pause();
    loadedClipIdRef.current = null; // allow clip-loading effect to reload
    seekOnLoadRef.current = null;
    setCurrentIndex(idx);
    setCurrentTime(0);
    setIsPlaying(shouldPlay);
  }, [singleSource]);

  // const goToClipAt = useCallback((idx: number, time: number, shouldPlay = false) => {
  //   const video = videoRef.current;
  //   if (video) video.pause();
  //   loadedClipIdRef.current = null; // allow clip-loading effect to reload
  //   seekOnLoadRef.current = { time, play: shouldPlay };
  //   setCurrentIndex(idx);
  //   setCurrentTime(time);
  //   setIsPlaying(shouldPlay);
  // }, []);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) goToClip(currentIndex - 1, isPlayingRef.current);
  }, [currentIndex, goToClip]);

  const handleNext = useCallback(() => {
    if (currentIndex < clips.length - 1) goToClip(currentIndex + 1, isPlayingRef.current);
  }, [currentIndex, clips.length, goToClip]);

  const handleEnded = useCallback(() => {
    /* Single-source: the file ending is the lesson ending — there is no next
       clip to advance to. */
    if (!singleSource && currentIndex < clips.length - 1) {
      goToClip(currentIndex + 1, true);
    } else {
      setIsPlaying(false);
      onEnded?.();
    }
  }, [singleSource, currentIndex, clips.length, goToClip, onEnded]);

  // TODO(thomas): fullscreen support removed — broken in delivered playback.
  // Multiple attempts (fullscreening the wrapper, fullscreening the <video>
  // element, prefixed fallbacks, dedicated `:fullscreen` CSS) all failed in
  // the live UI. The most likely root cause is a transformed / filtered /
  // perspective / will-change ancestor in the lesson-detail layout that
  // creates a containing block and blocks the Fullscreen API on
  // descendants. Re-implement when reworking the player — DevTools walk-up
  // of the ancestor chain on a paused breakdown video is the diagnostic
  // path. The fullscreen icon, handler, container ref, and `:fullscreen`
  // CSS rule were all stripped to avoid leaving dead code behind.

  /* Fired from handleTogglePlay only, which is the single funnel every
     user-initiated toggle goes through — the click layer, the bar's play
     button, and any keyboard shortcut added later. Deliberately NOT fired from
     the element's own play/pause events: the pipeline pauses and resumes at
     every clip boundary, and a mark flashing there would be reporting the
     machinery rather than the viewer's action. */
  const showBezel = useCallback((playing: boolean) => {
    setBezel((b) => ({ id: (b?.id ?? 0) + 1, playing }));
  }, []);

  const handleTogglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      // The clip-loading effect skips setting video.src when the user hasn't
      // played yet (to avoid CPU spin from Chrome's media pipeline). Set it
      // now on the first play request for the current clip. Single-source
      // mode never swaps src — its one file is loaded by its own effect.
      if (!singleSource && currentClip && loadedClipIdRef.current !== currentClip.id) {
        const url = getUrl(currentClip);
        if (!url) return;
        video.src = url;
        loadedClipIdRef.current = currentClip.id;
      }
      setIsPlaying(true);
      showBezel(true);
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
      setIsPlaying(false);
      showBezel(false);
    }
  }, [singleSource, currentClip, getUrl, showBezel]);

  /**
   * The timeline walk, lifted out of handleVirtualSeek verbatim so the scrubber
   * drag resolves a position exactly the way the range input does. Moved, not
   * rewritten: what a seek resolves to is unchanged.
   */
  const resolveVirtual = useCallback((vt: number) => {
    /* Single-source: the virtual position IS the element position. Every
       caller's "same clip already loaded" guard then compares index 0 against
       itself and seeks the one file directly. */
    if (singleSource) return { targetIdx: 0, targetTime: vt };

    let remaining = vt;
    let targetIdx = 0;
    let targetTime = 0;

    for (let i = 0; i < clips.length; i++) {
      const dur = getDur(clips[i]);
      if (remaining <= dur || i === clips.length - 1) {
        targetIdx = i;
        targetTime = Math.max(0, Math.min(remaining, dur));
        break;
      }
      remaining -= dur;
    }
    return { targetIdx, targetTime };
  }, [singleSource, clips, getDur]);

  // ── Virtual seek — maps a position on the combined timeline to the right clip
  const handleVirtualSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const vt = Number(e.target.value);
    const { targetIdx, targetTime } = resolveVirtual(vt);

    if (targetIdx === currentIndex) {
      // Same clip — seek the video element directly for immediate feedback.
      const video = videoRef.current;
      if (video) {
        if (loadedClipIdRef.current !== clips[targetIdx]?.id) {
          // Video src not yet set (classic mode before first play) — load and seek.
          const clip = clips[targetIdx];
          const url = clip ? getUrl(clip) : null;
          if (url) {
            loadedClipIdRef.current = clip!.id;
            seekOnLoadRef.current = { time: targetTime, play: false };
            video.src = url;
            video.load();
          }
        } else {
          video.currentTime = targetTime;
        }
        setCurrentTime(targetTime);
        // Propagate immediately so latestVirtualTimeRef in LessonEditPreview
        // is current even if timeupdate hasn't fired yet (paused-video seek).
        onVirtualTimeChangeRef.current?.(
          (clipOffsetsRef.current[targetIdx] ?? 0) + targetTime,
          totalDurationRef.current,
        );
      }
    } else {
      // Cross-clip drag: only update the visual scrubber position. The actual
      // video load is deferred to handleScrubCommit (pointerup) so that rapid
      // onChange events don't repeatedly restart the async media pipeline.
      isScrubbingRef.current = true;
      pendingScrubRef.current = { idx: targetIdx, time: targetTime };
      setCurrentIndex(targetIdx);
      setCurrentTime(targetTime);
    }
  }, [clips, currentIndex, getUrl, resolveVirtual]);

  // ── Commit a cross-clip scrub on pointer/mouse up
  // Fires once when the user lifts their finger/mouse after dragging across a
  // clip boundary. Clears the scrubbing lock, then triggers the clip-loading
  // effect for the committed target position.
  const handleScrubCommit = useCallback(() => {
    isScrubbingRef.current = false;
    const pending = pendingScrubRef.current;
    if (!pending) return;
    pendingScrubRef.current = null;

    // Clear the loaded-clip guard so the effect reloads even if currentIndex
    // didn't change in state (happens when the user drags to the same clip
    // index the state already holds from a previous drag).
    loadedClipIdRef.current = null;
    seekOnLoadRef.current = { time: pending.time, play: isPlayingRef.current };

    // Propagate immediately — the video hasn't loaded the new clip yet so
    // timeupdate won't fire before a potential hard refresh.
    const committedVt = (clipOffsetsRef.current[pending.idx] ?? 0) + pending.time;
    onPausedRef.current?.(committedVt, totalDurationRef.current);

    // Ensure currentIndex state matches (may already match from the drag update).
    setCurrentIndex(pending.idx);
    // Incrementing clipLoadTrigger forces the clip-loading effect to re-run
    // even when setCurrentIndex is a no-op (same value).
    setClipLoadTrigger((n) => n + 1);
  }, []);


  // ── Resume-from indicator (shown briefly after an initial seek)
  const [resumedAt, setResumedAt] = useState<number | null>(null);
  useEffect(() => {
    if (resumedAt === null) return;
    const t = setTimeout(() => setResumedAt(null), 2500);
    return () => clearTimeout(t);
  }, [resumedAt]);

  // ── Initial seek — restores a saved playback position once durations are ready
  const didSeekInitialRef = useRef(false);
  useEffect(() => {
    if (didSeekInitialRef.current) return;
    if (initialVirtualTime <= 0) return;
    if (totalDuration <= 0) return; // wait for durations to resolve

    didSeekInitialRef.current = true;

    if (singleSource) {
      /* One file: seek it directly if its metadata is in; otherwise park the
         position for the single-source load effect to consume. */
      const vt = Math.min(initialVirtualTime, totalDuration);
      const v = videoRef.current;
      if (v && v.readyState >= 1 && loadedClipIdRef.current !== null) {
        v.currentTime = vt;
      } else {
        seekOnLoadRef.current = { time: vt, play: false };
      }
      setCurrentTime(vt);
      setResumedAt(initialVirtualTime);
      return;
    }

    // Map virtualTime → clip index + offset within that clip
    let remaining = Math.min(initialVirtualTime, totalDuration);
    let targetIdx = 0;
    let targetTime = 0;
    for (let i = 0; i < clips.length; i++) {
      const dur = getDur(clips[i]);
      if (remaining <= dur || i === clips.length - 1) {
        targetIdx = i;
        targetTime = Math.min(remaining, dur);
        break;
      }
      remaining -= dur;
    }

    loadedClipIdRef.current = null;
    seekOnLoadRef.current = { time: targetTime, play: false };
    setCurrentIndex(targetIdx);
    setCurrentTime(targetTime);
    setResumedAt(initialVirtualTime);
    // Force the clip-loading effect to re-run even when targetIdx equals the
    // current clip index (which is almost always 0 for single-clip lessons).
    // Without this, setCurrentIndex is a no-op and the effect's deps never
    // change, so seekOnLoadRef is set but never consumed — the video element
    // stays at its poster-frame position (0.1s) and the seek never lands.
    setClipLoadTrigger((n) => n + 1);
  }, [initialVirtualTime, totalDuration, clips, getDur, singleSource]);

  // ── Resolved download URL (converts storage paths to HTTP URLs)
  const [resolvedDownloadUrl, setResolvedDownloadUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!downloadUrl) { setResolvedDownloadUrl(null); return; }
    if (downloadUrl.startsWith('http')) { setResolvedDownloadUrl(downloadUrl); return; }
    // Storage path — resolve to a signed download URL
    let cancelled = false;
    getStorageUrl(downloadUrl)
      .then((url) => { if (!cancelled) setResolvedDownloadUrl(url); })
      .catch((err) => { console.warn('LessonPreview: could not resolve download URL', err); });
    return () => { cancelled = true; };
  }, [downloadUrl]);

  /* ── Single-source load ──────────────────────────────────────────────────
     The composited MP4 is the element's one src for the whole session, loaded
     eagerly so a real poster frame paints — the same treatment the per-clip
     native path gives clip one. loadedClipIdRef carries the first clip's id so
     every "same clip already loaded" seek guard treats the file as loaded and
     seeks land directly on the element. */
  useEffect(() => {
    if (!singleSource) return;
    const video = videoRef.current;
    if (!video || !resolvedDownloadUrl) return;
    /* #t=0.001: iOS Safari will not decode a frame of an idle preload=metadata
       video, so without this the element sits black until first play. The
       fragment makes it render the opening frame; a programmatic seek after
       loadedmetadata (the desktop per-clip approach) does not paint there. */
    const src = `${resolvedDownloadUrl}#t=0.001`;
    if (video.src === src) return;

    loadedClipIdRef.current = clips[0]?.id ?? null;
    video.src = src;
    video.load();

    const handleMeta = () => {
      const seekOn = seekOnLoadRef.current;
      seekOnLoadRef.current = null;
      if (seekOn && seekOn.time > 0) {
        video.currentTime = seekOn.time;
        if (seekOn.play || autoPlayRef.current) video.play().catch(() => {});
      } else if (autoPlayRef.current) {
        video.play().catch(() => {});
      }
    };
    video.addEventListener('loadedmetadata', handleMeta, { once: true });
    return () => video.removeEventListener('loadedmetadata', handleMeta);
  }, [singleSource, resolvedDownloadUrl, clips]);

  const handleDownload = useCallback(async () => {
    if ((!resolvedDownloadUrl && !downloadStoragePath) || isDownloading) return;
    setIsDownloading(true);

    const triggerBlobDownload = (blob: Blob, filename: string) => {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so the browser has time to start the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    };

    const filename = (() => {
      const path = downloadStoragePath ?? resolvedDownloadUrl ?? '';
      const base = path.split('?')[0].split('/').pop() ?? '';
      return decodeURIComponent(base) || 'lesson.mp4';
    })();

    try {
      // Prefer Firebase Storage SDK — avoids CORS and handles auth transparently.
      if (downloadStoragePath) {
        const blob = await getBlob(storageRef(storage, downloadStoragePath));
        triggerBlobDownload(blob, filename);
        return;
      }
      // Fallback: fetch via URL (works when the URL is already a signed HTTP URL).
      if (resolvedDownloadUrl) {
        try {
          const res = await fetch(resolvedDownloadUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          triggerBlobDownload(blob, filename);
          return;
        } catch {
          // Last resort: open in a new tab so the browser's native save-as kicks in.
          window.open(resolvedDownloadUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (err) {
      console.error('LessonPreview: download failed', err);
    } finally {
      setIsDownloading(false);
    }
  }, [resolvedDownloadUrl, downloadStoragePath, isDownloading]);

  /* Coach, offering, date — middle dots because a coach's name can contain a
     comma, and a dot reads as a field separator rather than as prose. Any
     missing field drops with its separator rather than leaving a gap. */
  const lessonTitle = [
    coachName,
    offeringLabel,
    deliveredAt
      ? new Date(deliveredAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : null,
  ].filter(Boolean).join(' · ');

  // ── Native control state ────────────────────────────────────────────────
  const scrubRef = useRef<HTMLDivElement>(null);
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  const [pointerOverBar, setPointerOverBar] = useState(false);
  const [pointerIdle, setPointerIdle] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  /** null closed, 'root' the top list, otherwise the key of the open row. */
  const [settingsPage, setSettingsPage] = useState<string | null>(null);
  /**
   * The transient centre mark. `id` is the React key, so a second toggle
   * mounts a new element and the CSS animation starts from zero rather than
   * queueing behind the one in flight. Cleared on animationend, so it holds no
   * timer and cannot outlive its own animation.
   */
  const [bezel, setBezel] = useState<{ id: number; playing: boolean } | null>(null);
  /** The chapters panel. Never open at the same time as the settings menu. */
  const [chaptersOpen, setChaptersOpen] = useState(false);
  /**
   * Touch control visibility. Touch devices get an explicit model instead of
   * hover: a tap on the picture toggles the bar, playback drops it 3 seconds
   * after starting (or after a reveal while playing), and pause pins it up.
   * The timer lives in the effect below, keyed on both play state and
   * visibility, so every reveal while playing re-arms the same 3 seconds.
   */
  const [touchControlsVisible, setTouchControlsVisible] = useState(true);
  const touchHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);

  /* ── Scrubber drag ────────────────────────────────────────────────────────
     The handle, the played fills and the time readout are written straight to
     the DOM while a drag is in flight, and React state is reconciled once on
     release. Going through state per pointermove would schedule a render for
     every move and land the handle a frame or more behind the cursor.
     Nothing here calls a setter, so a drag causes no renders at all. */
  const dragRef = useRef<{ vt: number; idx: number; time: number } | null>(null);
  /** Whether the settings menu was open when the press that led to this click began. */
  const menuOpenAtPressRef = useRef(false);
  const handleElRef = useRef<HTMLDivElement>(null);
  const timeReadoutRef = useRef<HTMLSpanElement>(null);
  const chapterFillRefs = useRef<(HTMLDivElement | null)[]>([]);

  /* Depth-one seek gate. The pattern is the one VideoEditorPlayer already uses
     for its timeline: hold at most one pending position and issue it when the
     in-flight seek reports back, so seeks chain at the decoder's rate instead
     of piling up. This is a second copy rather than a call into that one —
     LessonPreview has never had a queue of its own, and the editor's lives
     inside its useImperativeHandle, reachable only by its own ref. */
  const isSeekingRef = useRef(false);
  const pendingSeekRef = useRef<number | null>(null);
  const queueSeek = useCallback((time: number, fast = false) => {
    const v = videoRef.current;
    if (!v) return;
    if (isSeekingRef.current) { pendingSeekRef.current = time; return; }
    /* Assigning the position it already holds fires no `seeked`, which would
       latch the gate shut for the rest of the drag. */
    if (Math.abs(v.currentTime - time) < SEEK_EPSILON) return;
    /* fastSeek lands on the nearest keyframe, which is what a live drag wants:
       Safari repaints per move instead of stalling on precise decodes. Only
       drags pass `fast` — a chapter tap or a release commit keeps the exact
       position via currentTime. Chrome has no fastSeek and falls through. */
    if (fast && typeof v.fastSeek === 'function') v.fastSeek(time);
    else v.currentTime = time;
    isSeekingRef.current = true;
  }, []);
  const gearRef = useRef<HTMLButtonElement>(null);

  /* Derived from the clips and the existing clipOffsets prefix-sum — the
     virtual timeline is untouched, this is a second view of it. */
  /**
   * One chapter per segment, not per clip.
   *
   * A coach flags a segment and records several clips against it; those are one
   * chapter, so a segment with three recordings is one continuous bar. Built
   * from *consecutive* runs of clips sharing segmentIds[0]: after the ordering
   * fix a segment's clips are always adjacent, but a draft saved before it can
   * hold the same segment twice. Those emit two chapters rather than being
   * merged — merging non-adjacent runs would make a chapter claim a stretch of
   * timeline it does not own.
   */
  const chapters: Chapter[] = useMemo(() => {
    const out: Chapter[] = [];
    clips.forEach((clip, i) => {
      const segId = clip.segmentIds[0];
      if (segId === undefined) {
        /* Unreachable through the current UI: every entry point builds
           primarySource from a NotableSegment, so a camera recording is always
           the secondary source and the primary's segment id is always present.
           If this fires it is old data or a new entry point that broke the
           convention — worth knowing rather than absorbing. */
        console.warn('[LessonPreview] clip has no segmentIds; chaptered on its own label', clip.id);
      }
      const start = clipOffsets[i] ?? 0;
      const end = start + getDur(clip);
      const prev = out[out.length - 1];
      if (prev && segId !== undefined && prev.segmentId === segId) {
        prev.endVirtual = end;   // same run — extend the bar rather than open another
        return;
      }
      /* segPos is the segment's position among segments, which is what "Part N"
         counts — not the chapter's position, which would renumber whenever a
         segment carried no clips. -1 means the id is not in `segments` at all,
         so there is no position to name and the clip's own label is all there
         is. */
      const segPos = segId ? segments.findIndex((sg) => sg.id === segId) : -1;
      const label = segPos >= 0
        ? (segments[segPos].label?.trim() || `Part ${segPos + 1}`)
        : getClipLabel(clip, segments);
      out.push({ index: out.length, segmentId: segId, startVirtual: start, endVirtual: end, label });
    });
    return out;
  }, [clips, clipOffsets, getDur, segments]);
  const chapterAt = useCallback(
    (vt: number) => chapters.find((c) => vt >= c.startVirtual && vt < c.endVirtual) ?? null,
    [chapters],
  );

  /* seekToVirtual lived here: it synthesised a range-input event so the chapter
     strip's onClick could reach handleVirtualSeek. The strip takes pointerdown
     now and a click is a drag with no moves, so it had no callers left, and
     seekFnsRef — which existed only to carry those two functions past the
     temporal dead zone — had no readers. */

  /**
   * Write the scrubber's position straight to the elements.
   *
   * Everything here is a style or a text write on a node we already hold, so it
   * lands in the same task as the pointer event that asked for it. No setState,
   * so React does not re-render and the handle cannot fall behind the cursor.
   * React reconciles these same three things from state on the next render after
   * release, and agrees with what is on screen because the reconcile passes the
   * position the drag ended at.
   */
  /* Scrub-to-pause for both scrub surfaces on this player: the custom chapter
     drag and the plain range input below it. Both drive the same element, so
     they share one instance — a drag that begins on one and ends on the other
     still resolves to a single begin/end pair. */
  const { begin: beginScrub, end: endScrub } = useScrubPause(() => videoRef.current);

  const paintScrub = useCallback((vt: number) => {
    const total = totalDurationRef.current;
    if (handleElRef.current && total > 0) {
      handleElRef.current.style.left = `calc(${(vt / total) * 100}% - 6px)`;
    }
    chapters.forEach((ch) => {
      const el = chapterFillRefs.current[ch.index];
      if (!el) return;
      const span = Math.max(0.0001, ch.endVirtual - ch.startVirtual);
      const p = Math.max(0, Math.min(1, (vt - ch.startVirtual) / span));
      el.style.width = `${p * 100}%`;
    });
    if (timeReadoutRef.current) {
      timeReadoutRef.current.textContent = `${formatTime(vt)} / ${formatTime(total)}`;
    }
  }, [chapters]);

  /* A drag causes no renders of its own, but something else can still render
     under it — onProgress reporting a new buffered end, the idle timer, the
     pointer entering the bar. React would then rebuild the handle and the fills
     from state, which is still at the pre-drag position, and the handle would
     jump back mid-drag. Repainting after every commit while a drag is live
     keeps the DOM authoritative for as long as the pointer owns it. */
  useEffect(() => {
    if (dragRef.current) paintScrub(dragRef.current.vt);
  });

  /* The time readout's text has one kind of owner: direct writes. It used to be
     React children as well, and paintScrub's textContent write replaced React's
     text nodes with its own. React kept updating the nodes it held, which were
     no longer in the document, so the readout froze at the last painted time
     for the rest of the session. Now the span has no children and React never
     touches its text; this writes it from state after every commit, before
     paint, and defers to the drag while one is live so a render under the
     pointer cannot pull it back. No dependency list: the span can remount when
     the controls do, and an unchanged-deps effect would leave it empty. */
  useLayoutEffect(() => {
    const el = timeReadoutRef.current;
    if (!el) return;
    const vt = dragRef.current ? dragRef.current.vt : virtualTime;
    const text = `${formatTime(vt)} / ${formatTime(totalDuration)}`;
    if (el.textContent !== text) el.textContent = text;
  });

  /**
   * One drag step, from pointerdown and from every move.
   *
   * Inside the clip already loaded, the seek goes out live so frames move under
   * the pointer — that clip is decoded and buffered, and the gate above keeps
   * the seeks from outrunning it. Landing in a different clip paints the handle
   * and the time but issues nothing: swapping `src` mid-drag restarts the media
   * pipeline on every move and the seek never lands. That swap waits for
   * release, which is what handleScrubCommit was written to do.
   */
  const dragTo = useCallback((clientX: number) => {
    const r = scrubRef.current?.getBoundingClientRect();
    const total = totalDurationRef.current;
    if (!r || r.width === 0 || total <= 0) return;
    const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const vt = pct * total;
    const { targetIdx, targetTime } = resolveVirtual(vt);
    dragRef.current = { vt, idx: targetIdx, time: targetTime };
    paintScrub(vt);
    const sameClipLoaded =
      targetIdx === currentIndex && loadedClipIdRef.current === clips[targetIdx]?.id;
    if (sameClipLoaded) queueSeek(targetTime, true);
  }, [resolveVirtual, paintScrub, currentIndex, clips, queueSeek]);

  /**
   * Commit a seek to a point on the virtual timeline, from something that is not
   * a drag — a chapter row.
   *
   * The two branches are endDrag's, and deliberately a second copy rather than a
   * refactor: the drag was out of scope for this pass, and rewriting it to share
   * this would have been a change to it. Worth merging next time either is
   * touched. The maths is resolveVirtual either way, so what a seek resolves to
   * cannot drift between them.
   */
  const seekToPoint = useCallback((vt: number) => {
    const { targetIdx, targetTime } = resolveVirtual(vt);
    paintScrub(vt);
    if (targetIdx === currentIndex && loadedClipIdRef.current === clips[targetIdx]?.id) {
      queueSeek(targetTime);
      setCurrentTime(targetTime);
      onVirtualTimeChangeRef.current?.(
        (clipOffsetsRef.current[targetIdx] ?? 0) + targetTime,
        totalDurationRef.current,
      );
    } else {
      pendingScrubRef.current = { idx: targetIdx, time: targetTime };
      setCurrentIndex(targetIdx);
      setCurrentTime(targetTime);
      handleScrubCommit();
    }
  }, [resolveVirtual, paintScrub, currentIndex, clips, queueSeek, handleScrubCommit]);

  const endDrag = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    isScrubbingRef.current = false;
    if (!d) return;
    if (d.idx === currentIndex && loadedClipIdRef.current === clips[d.idx]?.id) {
      /* Already seeked live; this brings state up to where the DOM is, and
         commits the exact position — the drag's live seeks were fastSeek,
         which lands on keyframes, so the release re-issues the precise one. */
      queueSeek(d.time);
      setCurrentTime(d.time);
      onVirtualTimeChangeRef.current?.(
        (clipOffsetsRef.current[d.idx] ?? 0) + d.time,
        totalDurationRef.current,
      );
    } else {
      /* Cross-clip: hand the target to handleScrubCommit exactly as the range
         input does, and let it do the load and the post-load seek. */
      pendingScrubRef.current = { idx: d.idx, time: d.time };
      setCurrentIndex(d.idx);
      setCurrentTime(d.time);
      handleScrubCommit();
    }
    setHoverPct(d.vt / (totalDurationRef.current || 1));
  }, [currentIndex, clips, handleScrubCommit, queueSeek]);

  /* Visible when paused, when the pointer is over the bar, or until 3s of
     stillness while playing. showOverlay keeps its existing meaning; this only
     adds the idle timer on top of it.
     The open settings menu pins it: the hidden bar also takes
     pointer-events-none, so a fade would leave the menu on screen and dead.
     Touch devices ignore every hover-derived term — pointerOverBar and
     showOverlay are set by mouse events, and iOS synthesises those on taps,
     which would pin the bar up forever. Their model is touchControlsVisible. */
  const controlsVisible = IS_TOUCH
    ? !isPlaying || settingsPage !== null || chaptersOpen || touchControlsVisible
    : !isPlaying || pointerOverBar || settingsPage !== null || chaptersOpen
      || (showOverlay && !pointerIdle);

  /* Idle timer. mousemove on the container resets it; three seconds of
     stillness while playing hides the bar. Cleared when paused so the bar can
     never disappear on a still frame. Mouse-driven, so touch devices skip it
     entirely — their hide timer is the effect below. */
  useEffect(() => {
    if (IS_TOUCH) return;
    if (!isPlaying) { setPointerIdle(false); return; }
    const reset = () => {
      setPointerIdle(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setPointerIdle(true), 3000);
    };
    reset();
    window.addEventListener('mousemove', reset);
    return () => {
      window.removeEventListener('mousemove', reset);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [isPlaying]);

  /* Touch hide timer. Pause pins the bar visible; while playing, any state
     where the bar is up gets 3 seconds and then drops. Keyed on visibility as
     well as play state so a tap-reveal mid-playback re-arms the timer. */
  useEffect(() => {
    if (!IS_TOUCH) return;
    if (touchHideTimerRef.current) {
      clearTimeout(touchHideTimerRef.current);
      touchHideTimerRef.current = null;
    }
    if (!isPlaying) {
      setTouchControlsVisible(true);
      return;
    }
    if (!touchControlsVisible) return;
    touchHideTimerRef.current = setTimeout(() => setTouchControlsVisible(false), 3000);
    return () => {
      if (touchHideTimerRef.current) clearTimeout(touchHideTimerRef.current);
    };
  }, [isPlaying, touchControlsVisible]);

  /* Playback-state notification for chrome outside the player. */
  useEffect(() => {
    onPlayingChangeRef.current?.(isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => setIsMuted((m) => !m), []);
  const setVolumeAndUnmute = useCallback((v: number) => { setVolume(v); setIsMuted(v === 0); }, []);

  /**
   * Fullscreen. Where element fullscreen exists (desktop, Android, iPad) the
   * parent's toggle keeps doing what it does today. iPhone Safari has neither
   * Element.requestFullscreen nor document.fullscreenEnabled, so the video
   * hands itself to the system player via webkitEnterFullscreen — close, PiP,
   * volume, skip and scrub all come from iOS, the way YouTube's mobile web
   * player does it. The element is not remounted by this, so on exit its
   * currentTime carries straight back into the inline player, and the pause
   * iOS issues on the way out lands in onPause and re-pins the control bar.
   *
   * In single-source mode the element holds the whole lesson, so the system
   * scrubber spans the lesson — the clip-length scrubber problem that
   * previously ruled webkitEnterFullscreen out does not apply to that file.
   */
  const handleFullscreenClick = useCallback(() => {
    const v = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    if (v && !document.fullscreenEnabled && typeof v.webkitEnterFullscreen === 'function') {
      try {
        v.webkitEnterFullscreen();
      } catch {
        /* Not ready (no metadata yet) — fall through to the parent's overlay
           rather than doing nothing. */
        onFullscreenToggle?.();
      }
      return;
    }
    onFullscreenToggle?.();
  }, [onFullscreenToggle]);
  const togglePip = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => {});
    else void v.requestPictureInPicture?.().catch(() => {});
  }, []);

  const settingsRows: SettingRow[] = useMemo(() => {
    const rows: SettingRow[] = [
      {
        key: 'speed',
        label: 'Playback Speed',
        valueLabel: speed === 1 ? 'Normal' : `${speed}`,
        options: SPEED_OPTIONS.map((o) => ({
          key: String(o.value),
          label: o.label,
          selected: speed === o.value,
          onSelect: () => setSpeed(o.value),
        })),
      },
    ];
    /* Omitted entirely where the browser has no picture-in-picture — iPhone
       Safari has none, and Firefox's own version is not this API. A row that
       cannot work should not be a row that does nothing. */
    if (typeof document !== 'undefined' && document.pictureInPictureEnabled) {
      rows.push({ key: 'pip', label: 'Picture in Picture', action: togglePip });
    }
    return rows;
  }, [speed, togglePip]);

  /* Outside click and Escape. pointerdown rather than click so a press that
     starts outside dismisses immediately, the way a native menu does; the gear
     is inside `settingsRef`, so its own toggle is not treated as an outside
     press and does not close-then-reopen. */
  useEffect(() => {
    if (settingsPage === null) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!settingsRef.current?.contains(e.target as Node)) setSettingsPage(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();   // fullscreen stays; Escape closes the menu first
      setSettingsPage(null);
      gearRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [settingsPage]);

  /* Escape closes the panel and stops there. Captured, like the settings menu's,
     so the browser does not also read it as "leave fullscreen". */
  useEffect(() => {
    if (!chaptersOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setChaptersOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [chaptersOpen]);

  /* Applied to the element rather than held only in React state, so a clip swap
     does not reset them — the new src inherits volume, mute and rate. */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
    v.muted = isMuted;
    v.playbackRate = speed;
  }, [volume, isMuted, speed, currentIndex]);


  // ── Loading / error states
  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="w-6 h-6 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
          <span className="text-sm">Loading lesson preview…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <p className="text-red-500 text-sm">{loadError}</p>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className={`flex items-center justify-center p-8 bg-gray-50 rounded-lg ${className}`}>
        <p className="text-gray-500 text-sm">No clips to preview yet.</p>
      </div>
    );
  }

  const clipLabel = currentClip ? getClipLabel(currentClip, segments) : '';
  const totalClips = clips.length;

  /** The open row, or null on the root list. */
  const activeSettingRow =
    settingsPage && settingsPage !== 'root'
      ? settingsRows.find((r) => r.key === settingsPage && r.options) ?? null
      : null;

  /**
   * The chapters list. One definition, two mount points: a real column beside
   * the video in fullscreen, an overlay inside it windowed.
   *
   * Proportions are YouTube's, measured in its fullscreen at 1512px — 72 row,
   * 100x56 thumbnail at radius 8 with 16 to its left, title at 14/20 weight 500,
   * a 36x22 timestamp chip at 12/18 weight 500 and radius 2, panel radius 12.
   * The colours are not theirs: their panel is white page chrome, ours is the
   * dark surface the settings menu already established, so the two overlays in
   * this player look like the same product.
   *
   * The thumbnail comes from the chapter's first clip, which is resolveVirtual
   * on the chapter's own start — no new field on the chapter, and the same walk
   * a seek uses. ClipThumbnail is reused exactly as the editor uses it.
   */
  const chaptersList = (
    <div className="flex h-full flex-col overflow-hidden" style={{ borderRadius: 12, background: 'rgba(28,28,28,0.92)', backdropFilter: 'blur(16px)' }}>
      <div className="flex items-center justify-between px-4 shrink-0" style={{ height: 52 }}>
        {/* Header copy and its 52 height are proposed, not measured: the header
            I could reach on YouTube belonged to a different panel. */}
        <span className="text-white font-semibold" style={{ fontSize: 15, lineHeight: '20px' }}>In this lesson</span>
        <button
          type="button"
          onClick={() => setChaptersOpen(false)}
          aria-label="Close the lesson parts list"
          className="w-9 h-9 -mr-2 rounded-full flex items-center justify-center text-white hover:bg-white/10"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto pb-2">
        {chapters.map((ch) => {
          const current = virtualTime >= ch.startVirtual && virtualTime < ch.endVirtual;
          /* The chapter's first clip, from the same walk a seek uses. The time
             within it is not needed here: the thumbnail is captured at its own
             offset, not at the chapter's start. */
          const { targetIdx } = resolveVirtual(ch.startVirtual);
          const clip = clips[targetIdx];
          return (
            <button
              key={ch.index}
              type="button"
              /* Seeks and stays open, so a viewer can move between parts
                 without reopening the list. The highlight follows on its own:
                 `current` reads virtualTime, and seekToPoint sets the state it
                 comes from. */
              onClick={() => seekToPoint(ch.startVirtual)}
              aria-current={current ? 'true' : undefined}
              className={`w-full flex items-center gap-3 pr-2 text-left ${
                current ? 'bg-white/10' : 'hover:bg-white/5'
              }`}
              style={{ height: 72, paddingTop: 8, paddingBottom: 8 }}
            >
              <div className="shrink-0 ml-4 overflow-hidden bg-black" style={{ width: 100, height: 56, borderRadius: 8 }}>
                <ChapterThumb
                  clipId={clip?.id ?? `missing-${ch.index}`}
                  url={clip ? getUrl(clip) ?? undefined : undefined}
                  storedDataUrl={clip?.thumbnailDataUrl}
                  duration={clip ? getDur(clip) : 0}
                />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <span className="truncate text-white" style={{ fontSize: 14, lineHeight: '20px', fontWeight: 500 }}>
                  {ch.label ?? `Part ${ch.index + 1}`}
                </span>
                <span
                  className={`self-start tabular-nums ${current ? 'text-white' : 'text-white/70'}`}
                  style={{
                    fontSize: 12, lineHeight: '18px', fontWeight: 500, borderRadius: 2, padding: '2px 6px',
                    background: current ? 'var(--color-biscayne)' : 'rgba(255,255,255,0.10)',
                  }}
                >
                  {formatTime(ch.startVirtual)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col ${className}`}>
      {/* ── Video player ──
          The row wrapper is rendered unconditionally, even windowed where it is
          a plain block. It has to be: React reconciles by position, and adding a
          wrapper around the video only when the panel opens would unmount and
          remount the <video>, dropping playback and the loaded clip. This is the
          same hazard the fullscreen `contents` wrapper was written around. */}
      <div className={isFullscreen ? 'flex-1 min-h-0 flex' : 'w-full'}>
      <div
        /* Fullscreen: fill the height between the title bar and the controls so
           object-contain centres the picture in it. aspect-video would pin a
           16:9 box to the top of a taller area and leave all the black below,
           which is the "video sits too high" problem.
           `min-w-0` and `h-full` fullscreen because the parent is now a row: the
           picture yields width to the chapters column and object-contain
           re-centres it in what is left, which is exactly what YouTube does. */
        className={`relative bg-black overflow-hidden w-full ${
          isFullscreen ? 'flex-1 min-w-0 h-full' : 'rounded-lg aspect-video'
        }`}
        onMouseEnter={() => setShowOverlay(true)}
        onMouseLeave={() => setShowOverlay(false)}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          /* Covers the gap before any frame decodes — most visible on iOS,
             where nothing paints until the #t fragment resolves. The stored
             160x90 capture upscales soft, but soft beats black. */
          poster={currentClip?.thumbnailDataUrl ?? clips[0]?.thumbnailDataUrl}
          onSeeked={(e) => {
            /* Drain the depth-one gate: whatever position arrived while this
               seek was in flight goes out now. */
            isSeekingRef.current = false;
            const next = pendingSeekRef.current;
            pendingSeekRef.current = null;
            if (next !== null && Math.abs(e.currentTarget.currentTime - next) >= SEEK_EPSILON) {
              e.currentTarget.currentTime = next;
              isSeekingRef.current = true;
            }
          }}
          onTimeUpdate={(e) => {
            /* A live seek fires timeupdate, and this setter would then re-render
               on every move. The drag paints the readout itself and reconciles
               state on release. */
            if (dragRef.current) return;
            const t = e.currentTarget.currentTime;
            setCurrentTime(t);
            onVirtualTimeChange?.((clipOffsets[currentIndex] ?? 0) + t, totalDuration);
          }}
          onProgress={(e) => {
            const v = e.currentTarget;
            setBufferedEnd(v.buffered.length ? v.buffered.end(v.buffered.length - 1) : 0);
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={(e) => {
            setIsPlaying(false);
            if (!isClipLoadingRef.current) {
              const t = e.currentTarget.currentTime;
              onPaused?.((clipOffsets[currentIndex] ?? 0) + t, totalDuration);
            }
          }}
          onEnded={handleEnded}
          onError={(e) => {
            console.error('[LessonPreview] clip video error', currentIndex, currentClip?.id, e.currentTarget.error);
            // Advance past the unplayable clip rather than stalling forever.
            handleEnded();
          }}
          preload={isNative ? 'metadata' : 'none'}
          playsInline
        />

        {/* Classic-mode clip label — overlaid on the video for the editor
            preview path. Native variant renders this as plain text BELOW
            the video frame instead (see block after the wrapper). */}
        {clipLabel && !isNative && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3 pointer-events-none">
            <p className="text-white text-sm font-medium drop-shadow">{clipLabel}</p>
            {totalClips > 1 && (
              <p className="text-white/80 text-xs drop-shadow">Clip {currentIndex + 1} of {totalClips}</p>
            )}
          </div>
        )}

        {/* ── Click-to-toggle, and the centre play/pause ───────────────────
            Scoped by paint order rather than by inspecting the event target.
            This layer covers the picture and is declared BEFORE the title bar,
            the scrim and the control bar, so all three sit above it and take
            their own clicks — including every button inside the bar, the
            scrubber and the settings panel. Nothing here has to know what a
            control is, and a control added to the bar later is covered without
            anyone remembering to add it to a list.

            The scrubber drag cannot reach this either, for a second reason: it
            takes pointer capture, so its pointerup retargets to the scrub row
            and the click is dispatched there. A drag that ends over the picture
            never produces a click on this layer. */}
        {isNative && showControls && (
          <>
            <div
              className="absolute inset-0"
              onPointerDown={() => {
                menuOpenAtPressRef.current = settingsPage !== null || chaptersOpen;
              }}
              onClick={() => {
                /* A click that dismisses the settings menu dismisses it and
                   does nothing else — one click, one effect. Read at
                   pointerdown because the document listener that closes the
                   menu has already run by the time click fires, so by then
                   settingsPage is null and the click looks innocent. */
                if (menuOpenAtPressRef.current) {
                  menuOpenAtPressRef.current = false;
                  setChaptersOpen(false);   /* the settings menu closes itself, on the document listener */
                  return;
                }
                /* Touch: a tap on the picture toggles the bar and never the
                   playback — play and pause live on the play button, the way
                   YouTube's mobile surface works. The hide timer effect
                   re-arms itself off this state change. */
                if (IS_TOUCH) {
                  setTouchControlsVisible((v) => !v);
                  return;
                }
                handleTogglePlay();
              }}
            />

            {/* The bezel: a mark, not a control. It has no click target and no
                tie to controlsVisible — it appears on a toggle, runs its 500ms
                animation and unmounts. Keyed by id, so a second toggle mounts a
                fresh element and the animation restarts instead of queueing.
                Geometry measured off YouTube's .ytp-bezel: 78 across, radius
                39, rgba(0,0,0,.6) behind blur(16px), a 54 glyph inside.
                pointer-events-none on both the centring wrapper and the mark,
                so a rapid second click passes straight through to the layer
                beneath while this is still on screen. */}
            {bezel && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  key={bezel.id}
                  className="player-bezel rounded-full flex items-center justify-center bg-black/60 backdrop-blur-[16px] pointer-events-none"
                  style={{ width: 78, height: 78 }}
                  onAnimationEnd={() => setBezel(null)}
                  aria-hidden="true"
                >
                  {bezel.playing ? (
                    /* Still nudged right by 1: a triangle's visual centre is
                       left of its bounding box's centre. */
                    <svg width="54" height="54" className="text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  ) : (
                    <svg width="54" height="54" className="text-white" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                    </svg>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Fullscreen title bar. Same scrim treatment as the control bar,
            mirrored. Fullscreen only: windowed, the detail view above the
            player already shows all three fields. */}
        {isNative && isFullscreen && lessonTitle && (
          <div
            className={`absolute top-0 left-0 right-0 flex items-start transition-opacity duration-200 ${
              controlsVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ height: 92, background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)' }}
          >
            <p className="text-white font-medium px-6 pt-5 truncate" style={{ fontSize: 18 }}>
              {lessonTitle}
            </p>
          </div>
        )}

        {/* ── Native controls ─────────────────────────────────────────────
            Overlaid on the video with its own gradient scrim. Geometry from a
            1440px YouTube player: 59 bar, 56 button row, 6 progress container,
            4 played fill, 12 handle, 4 between chapter segments. The bar does
            not thicken on hover — measured 6px at rest and 6px hovering with
            no transform — so there is no thicken effect here either.

            Tooltip offset is 18px above the bar, not YouTube's 89: theirs
            clears a preview filmstrip, ours holds one line of timestamp. */}
        {/* The scrim is its own layer, not the bar's background: it is 120 tall
            against the bar's 59, so it has to run above the bar's box. Declared
            before the bar so the bar's controls paint over it — both are
            absolutely positioned, so document order decides, and no z-index is
            needed. pointer-events-none keeps the 61px of scrim above the bar
            from swallowing clicks meant for the video. */}
        {isNative && showControls && (
          <div
            className={`absolute bottom-0 left-0 right-0 pointer-events-none transition-opacity duration-200 ${
              controlsVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ height: SCRIM_H, background: SCRIM_GRADIENT }}
          />
        )}

        {isNative && showControls && (
          <div
            className={`absolute bottom-0 left-0 right-0 transition-opacity duration-200 ${
              controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{ height: CONTROL_BAR_H, paddingTop: SCRUBBER_TOP_INSET }}
            onMouseEnter={() => setPointerOverBar(true)}
            onMouseLeave={() => setPointerOverBar(false)}
          >
            {/* ── Scrubber, above the buttons and inset by the bar's padding ──
                The row keeps its 6px visual height; the pointer surface is the
                overlay declared as its last child. */}
            <div
              ref={scrubRef}
              className="relative mx-4"
              style={{ height: SCRUBBER_ROW_H }}
            >
              {/* One bar per chapter, so a segment recorded three times reads as
                  one bar rather than three. Sized by its share of the timeline;
                  each fills independently as playback passes through it. */}
              <div className="absolute inset-0 flex items-center" style={{ gap: 4 }}>
                {chapters.map((ch) => {
                  const span = Math.max(0.0001, ch.endVirtual - ch.startVirtual);
                  const played = Math.max(0, Math.min(1, (virtualTime - ch.startVirtual) / span));
                  /* Buffered is per-element and clip-scoped, so it only means
                     anything inside the chapter being played. */
                  const inThis = virtualTime >= ch.startVirtual && virtualTime < ch.endVirtual;
                  const buffered = inThis
                    ? Math.max(played, Math.min(1, ((clipOffsets[currentIndex] ?? 0) - ch.startVirtual + bufferedEnd) / span))
                    : (virtualTime >= ch.endVirtual ? 1 : 0);
                  return (
                    <div
                      key={ch.index}
                      className="relative overflow-hidden rounded-full"
                      /* The hairline, not the fill, is what makes the bar read on
                         a sunlit court. A white track over a bright frame cannot
                         win: the scrim leaves the backdrop at rgb 173 there, so
                         even a fully opaque white track tops out at 2.12:1, and
                         going from 0.25 to 0.35 moved it 1.26 → 1.37. A 1px
                         black ring reads 3.83:1 against that same backdrop.
                         0.30 rather than 0.35 because the buffered fill is white
                         at 0.4 and sits on top of this: at 0.35 the two start
                         collapsing into each other. Over a dark frame the ring
                         disappears into the scrim and the white track carries it,
                         which is the way round it should fail. */
                      style={{
                        flexGrow: span, flexBasis: 0, height: 4,
                        background: 'rgba(255,255,255,0.30)',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.55)',
                      }}
                    >
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{ width: `${buffered * 100}%`, background: 'rgba(255,255,255,0.4)' }}
                      />
                      <div
                        ref={(el) => { chapterFillRefs.current[ch.index] = el; }}
                        className="absolute inset-y-0 left-0"
                        style={{ width: `${played * 100}%`, background: 'var(--color-biscayne)' }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Handle — visible at rest, per the measurement. */}
              {totalDuration > 0 && (
                <div
                  ref={handleElRef}
                  className="absolute rounded-full pointer-events-none"
                  style={{
                    width: 12, height: 12, top: -3,
                    left: `calc(${(virtualTime / totalDuration) * 100}% - 6px)`,
                    background: 'var(--color-biscayne)',
                  }}
                />
              )}

              {/* Chapter name over the timestamp, the order YouTube uses. Two
                  lines rather than one run-on: the name is variable width and
                  the time is not, so stacking keeps the time from shifting
                  under the cursor as the name changes. */}
              {hoverPct !== null && totalDuration > 0 && (
                <div
                  className="absolute -translate-x-1/2 whitespace-nowrap rounded px-2 py-1 pointer-events-none"
                  style={{
                    left: `${hoverPct * 100}%`, bottom: 18,
                    background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: 13,
                  }}
                >
                  {chapterAt(hoverPct * totalDuration)?.label && (
                    <span className="block max-w-[220px] truncate text-center">
                      {chapterAt(hoverPct * totalDuration)?.label}
                    </span>
                  )}
                  <span className="block text-center tabular-nums text-white/70">
                    {formatTime(hoverPct * totalDuration)}
                  </span>
                </div>
              )}

              {/* ── Hit surface ─────────────────────────────────────────────
                  44pt tall against the track's visible 6, per the platform
                  minimum — a fingertip could not land on the bare track, which
                  is half of why touch scrubbing felt broken. Extended upward
                  over the picture and only 4px below the track, stopping where
                  the button row's controls begin so it can never eat their
                  taps. Declared last, so it sits above the fills, the handle
                  and the tooltip.

                  touch-action: none is the other half: without it the browser
                  claims a touch drag for page scrolling and fires
                  pointercancel, dropping the scrub mid-gesture. With it, plus
                  setPointerCapture, the gesture belongs to the scrubber from
                  down to up.

                  X maths use scrubRef's rect (the track), not this box — the
                  two share left/right edges, so the positions agree; only the
                  height differs. */}
              <div
                className="absolute cursor-pointer"
                style={{ left: 0, right: 0, top: -34, height: 44, touchAction: 'none' }}
                onMouseMove={(e) => {
                  /* Suppressed mid-drag: this is a setState, and one per move
                     is exactly the per-move render the drag is written to
                     avoid. The tooltip is hidden for the duration and restored
                     on release. Suppressed on touch: iOS synthesises a
                     mousemove from a tap and the tooltip would flash with no
                     cursor to anchor it. */
                  if (IS_TOUCH || dragRef.current) return;
                  const r = scrubRef.current?.getBoundingClientRect();
                  if (!r || r.width === 0) return;
                  setHoverPct(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                }}
                onMouseLeave={() => { if (!dragRef.current) setHoverPct(null); }}
                /* pointerdown seeks and starts the drag, so a plain click is
                   just a drag with no moves and needs no onClick of its own.
                   isScrubbingRef holds off the clip-loading effect for the
                   duration, the same lock the range input takes. */
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  isScrubbingRef.current = true;
                  beginScrub();
                  setHoverPct(null);
                  dragTo(e.clientX);
                }}
                onPointerMove={(e) => { if (dragRef.current) dragTo(e.clientX); }}
                onPointerUp={(e) => {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                  endDrag();
                  /* After endDrag, not before. A cross-clip release hands the
                     target to handleScrubCommit, which loads the new clip and
                     seeks it; resuming first would play the old clip for a
                     frame before the swap. */
                  endScrub();
                }}
                onPointerCancel={() => { endDrag(); endScrub(); }}
              />
            </div>

            {/* ── Button row ── */}
            <div className="flex items-center justify-between px-4" style={{ height: BUTTON_ROW_H }}>
              {/* min-w-0 here and on the chapter pill, shrink-0 on the right
                  group: under width pressure the chapter label truncates and the
                  controls stay put, instead of the fixed-width right group being
                  pushed past the bar's edge and clipped. Nothing moves at widths
                  where everything already fits, and the right group survives to
                  340 where it used to start clipping below about 580. That was
                  true before the parts-list button was added and after it was
                  removed again — the guard is not about that button. */}
              <div className="flex items-center gap-3 min-w-0">
                <button type="button" onClick={handleTogglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10">
                  {isPlaying
                    ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>}
                </button>

                {/* Volume: mute toggle plus a slider that appears on hover. */}
                <div className="flex items-center gap-1 group/vol">
                  <button type="button" onClick={toggleMute} aria-label={isMuted || volume === 0 ? 'Unmute' : 'Mute'}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10">
                    {isMuted || volume === 0
                      ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                      : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>}
                  </button>
                  {/* Hover-revealed, so it has no way in on touch — the mute
                      toggle carries volume there and the system rocker does
                      the rest. */}
                  {!IS_TOUCH && (
                    <input type="range" min={0} max={1} step={0.05} value={isMuted ? 0 : volume}
                      onChange={(e) => setVolumeAndUnmute(Number(e.target.value))}
                      aria-label="Volume"
                      className="w-0 opacity-0 group-hover/vol:w-16 group-hover/vol:opacity-100 transition-all duration-150 h-1 accent-white cursor-pointer" />
                  )}
                </div>

                {/* whitespace-nowrap and shrink-0: the string has spaces around
                    the slash, so its min-content width was the widest run
                    ("4:49"), not the whole readout — under pressure the flex
                    item shrank to that and the text wrapped to two lines. It is
                    a fixed-width fact and never shrinks now.
                    No React children on purpose: the text is written only by
                    the layout effect beside paintScrub and by paintScrub itself.
                    See that effect for why. */}
                <span
                  ref={timeReadoutRef}
                  className="text-white tabular-nums whitespace-nowrap shrink-0"
                  style={{ fontSize: 14 }}
                />
                {/* A button, not a label: this opens the chapters panel once
                    that exists, and it carries the affordance now. Hover fill is
                    YouTube's, measured off its chapter pill —
                    rgba(255,255,255,.1), which is the same white/10 every other
                    control in this bar already uses. Theirs is a 48-high pill at
                    radius 28; ours is the bar's own 36 at radius 100. */}
                {/* Below md the name is the first thing to lose: truncated to
                    "Se…" it said nothing and still cost the width the time
                    readout needed. The icon opens the same panel. No chevron on
                    it — beside a list glyph a chevron reads as "expand" and
                    doubles the control's width to repeat what the icon says. */}
                {chapterAt(virtualTime)?.label && (
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsPage(null);
                      setChaptersOpen((open) => !open);
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={chaptersOpen}
                    aria-label="In this lesson"
                    className={`md:hidden w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white hover:bg-white/10 ${
                      chaptersOpen ? 'bg-white/10' : ''
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                    </svg>
                  </button>
                )}
                {chapterAt(virtualTime)?.label && (
                  <button
                    type="button"
                    onClick={() => {
                      /* One overlay at a time. */
                      setSettingsPage(null);
                      setChaptersOpen((open) => !open);
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={chaptersOpen}
                    /* The open state is carried by the same white/10 fill the
                       gear uses when its menu is up, not by the chevron. */
                    className={`hidden md:flex h-9 pl-3 pr-1 rounded-full items-center gap-0 text-white/70 hover:bg-white/10 hover:text-white transition-colors min-w-0 max-w-[240px] ${
                      chaptersOpen ? 'bg-white/10 text-white' : ''
                    }`}
                    style={{ fontSize: 14 }}
                  >
                    <span className="truncate">{chapterAt(virtualTime)?.label}</span>
                    {/* Measured off YouTube's .ytp-chapter-title-chevron: a 20px
                        box, no margin, zero gap from the label — the glyph's own
                        inset inside a 24 viewBox supplies the spacing — and the
                        label's colour rather than a dimmer one.
                        It does not rotate, and theirs does not either: measured
                        `transform: none` on both the box and the svg with the
                        panel open. Rotation would promise a disclosure opening
                        downward, and this panel opens to the side. */}
                    <svg
                      className="shrink-0"
                      width="20" height="20" viewBox="0 0 24 24"
                      fill="currentColor" aria-hidden="true"
                    >
                      <path d="M9.71 18.71l-1.42-1.42 5.3-5.29-5.3-5.29 1.42-1.42 6.7 6.71z" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* ── Settings, in YouTube's position: before picture-in-picture
                    and fullscreen. Two levels — a root list of rows, and one
                    row's options — anchored above the gear.
                    Panel geometry from YouTube at 1372px: radius 12, 48-high
                    rows, 14px label, value at white/70. Width is 240 rather
                    than their 328; theirs holds eight rows and ours holds one,
                    and maxHeight is set now so a longer list scrolls instead of
                    running off the top of the frame. */}
                <div className="relative" ref={settingsRef}>
                  <button
                    ref={gearRef}
                    type="button"
                    onClick={() => {
                      setChaptersOpen(false);   /* one overlay at a time */
                      setSettingsPage((page) => (page === null ? 'root' : null));
                    }}
                    aria-haspopup="menu"
                    aria-expanded={settingsPage !== null}
                    aria-label="Settings"
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10 ${
                      settingsPage !== null ? 'bg-white/10' : ''
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.74 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>
                    </svg>
                  </button>

                  {settingsPage !== null && (
                    <div
                      role="menu"
                      aria-label="Settings"
                      /* Surface values from YouTube: rgba(28,28,28,0.9) on the
                         classic popup, radius 12 and blur(16px) on the current
                         one, 8px of vertical padding on the menu.
                         28,28,28 rather than the pure black this had: over a
                         letterboxed frame an 85%-black panel on black rendered
                         as bare text with nothing behind it. The hairline and
                         the shadow are proposed, not measured — YouTube has
                         neither, and needs neither, because its menu never
                         opens over its own letterbox. */
                      className="absolute bottom-full right-0 mb-2 overflow-y-auto"
                      style={{
                        width: 240, maxHeight: 320, borderRadius: 12, padding: '8px 0',
                        background: 'rgba(28,28,28,0.9)', backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                      }}
                    >
                      {activeSettingRow === null
                        ? settingsRows.map((row) => (
                            <button
                              key={row.key}
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                if (row.action) { row.action(); setSettingsPage(null); return; }
                                setSettingsPage(row.key);
                              }}
                              className="w-full flex items-center justify-between gap-3 px-4 text-white hover:bg-white/10 text-left"
                              style={{ height: 48, fontSize: 14 }}
                            >
                              <span className="truncate">{row.label}</span>
                              {!row.action && (
                                <span className="flex items-center gap-1 text-white/70 shrink-0">
                                  {row.valueLabel}
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <polyline points="9 6 15 12 9 18" />
                                  </svg>
                                </span>
                              )}
                            </button>
                          ))
                        : (
                          <>
                            <button
                              type="button"
                              onClick={() => setSettingsPage('root')}
                              className="w-full flex items-center gap-2 px-3 text-white hover:bg-white/10 text-left border-b border-white/15"
                              style={{ height: 48, fontSize: 14 }}
                            >
                              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                <polyline points="15 6 9 12 15 18" />
                              </svg>
                              <span className="truncate">{activeSettingRow.label}</span>
                            </button>
                            {(activeSettingRow.options ?? []).map((opt) => (
                              <button
                                key={opt.key}
                                type="button"
                                role="menuitemradio"
                                aria-checked={opt.selected}
                                onClick={() => { opt.onSelect(); setSettingsPage('root'); }}
                                className="w-full flex items-center gap-2 px-3 text-white hover:bg-white/10 text-left"
                                style={{ height: 48, fontSize: 14 }}
                              >
                                {/* The tick's box is always there, so the labels
                                    do not shift as the selection moves. */}
                                <span className="w-4 shrink-0">
                                  {opt.selected && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </span>
                                <span className="truncate">{opt.label}</span>
                              </button>
                            ))}
                          </>
                        )}
                    </div>
                  )}
                </div>

                {resolvedDownloadUrl && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    aria-label="Download video"
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDownloading
                      ? <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                      : <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>}
                  </button>
                )}

                <button type="button" onClick={handleFullscreenClick} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10">
                  {isFullscreen
                    ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
                    : <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Windowed, the panel overlays the picture instead of taking a column.
            PROPOSED, not measured, and the one call here I could not take from
            the reference: windowed the player's width is set by the page around
            it, so a 400 column would cut the picture roughly in half, and unlike
            fullscreen there is no spare screen to give it. An overlay keeps the
            picture whole and is dismissed by clicking it, which is the natural
            windowed gesture. Say the word and it becomes a column, or moves
            below the player as a list.
            Declared after the control bar, so it is above the click layer and a
            click inside it never reaches the toggle. */}
        {isNative && chaptersOpen && !isFullscreen && (
          <div className="absolute right-3 top-3 bottom-3 z-10" style={{ width: 340 }}>
            {chaptersList}
          </div>
        )}
      </div>

      {/* Fullscreen, it is a real column and the picture yields the width.
          Measured off YouTube at 1512px: their slot is 416 with the panel 382
          inside it, 17 clear on each side. Ours is 412 with 16. */}
      {isNative && chaptersOpen && isFullscreen && (
        <aside className="shrink-0 h-full p-4" style={{ width: 412 }}>
          {chaptersList}
        </aside>
      )}
      </div>

      {/* ── Native variant: clip title + clip-number as plain text below
          the video frame, NOT overlaid on the video. */}

      {/* ── Classic controls (legacy editor preview path) ── */}
      {!isNative && showControls && (
        <div className="mt-2 space-y-1">
          {/* Combined seek bar */}
          <div className="relative">
            <input
              type="range"
              min={0}
              max={totalDuration > 0 ? totalDuration : 1}
              step={0.05}
              value={virtualTime}
              onChange={handleVirtualSeek}
              onPointerDown={beginScrub}
              onPointerUp={() => { handleScrubCommit(); endScrub(); }}
              onPointerCancel={endScrub}
              onKeyDown={beginScrub}
              onKeyUp={() => { handleScrubCommit(); endScrub(); }}
              onBlur={endScrub}
              disabled={isProbing || totalDuration === 0}
              className="w-full h-1 accent-blue-500 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            />
            {/* Per-clip boundary markers on the seek bar */}
            {/* Temporarily suspended: tick marks hidden so the timeline appears
                unbroken. Re-enable by removing the `false &&` guard. */}
            {false && totalClips > 1 && totalDuration > 0 && clipOffsets.slice(1).map((offset, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-px bg-white/60 pointer-events-none"
                style={{ left: `${(offset / totalDuration) * 100}%` }}
              />
            ))}
          </div>

          {/* Button row */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Previous clip"
              aria-label="Previous clip"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>

            <button
              onClick={handleTogglePlay}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700 transition-colors"
              title={isPlaying ? 'Pause' : 'Play'}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            <button
              onClick={handleNext}
              disabled={currentIndex === clips.length - 1}
              className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Next clip"
              aria-label="Next clip"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>

            <span className="text-xs text-gray-500 font-mono ml-1">
              {isProbing
                ? <span className="text-gray-400">Calculating…</span>
                : <>{formatTime(virtualTime)} / {formatTime(totalDuration)}</>
              }
            </span>

            <div className="flex-1" />

            {resolvedDownloadUrl && (
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Download lesson video"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isDownloading ? 'Downloading…' : 'Download'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Coach notes ── */}
      {notes && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          {coachName && (
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Notes from {coachName}
            </p>
          )}
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
        </div>
      )}
    </div>
  );
}
